import { type Component, type JSX, Show } from "solid-js";
import { cn } from "@stockpilot/shared";

/**
 * Estado de existencias codificado por color (identidad WMS de Stockpilot):
 *  · ok        — stock por encima del punto de reorden (verde)
 *  · low       — stock bajo / en/por debajo del reorden (ámbar)
 *  · out       — agotado (rojo)
 *  · expiring  — lote por vencer (naranja FEFO)
 */
type StockStatus = "ok" | "low" | "out" | "expiring";

const STATUS_CLASS: Record<StockStatus, string> = {
  ok: "bg-stock-ok-bg text-stock-ok",
  low: "bg-stock-low-bg text-stock-low",
  out: "bg-stock-out-bg text-stock-out",
  expiring: "bg-stock-expiring-bg text-stock-expiring",
};

const DOT_CLASS: Record<StockStatus, string> = {
  ok: "bg-stock-ok",
  low: "bg-stock-low",
  out: "bg-stock-out",
  expiring: "bg-stock-expiring",
};

/**
 * Deriva el estado de stock a partir de la cantidad disponible y el punto de
 * reorden. Centraliza la regla para que catálogo/existencias/reabastecimiento
 * codifiquen igual.
 */
export function deriveStockStatus(quantity: number, reorderPoint = 0): StockStatus {
  if (quantity <= 0) {
    return "out";
  }
  if (reorderPoint > 0 && quantity <= reorderPoint) {
    return "low";
  }
  return "ok";
}

interface StockBadgeProps {
  status: StockStatus;
  label: string;
  class?: string;
  /** Texto accesible alternativo (default = label). */
  "aria-label"?: string;
  children?: JSX.Element;
}

export const StockBadge: Component<StockBadgeProps> = (props) => (
  <span
    class={cn(
      "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap",
      STATUS_CLASS[props.status],
      props.class,
    )}
    role="status"
    aria-label={props["aria-label"] ?? props.label}
    data-stock-status={props.status}
  >
    <span
      class={cn("h-1.5 w-1.5 shrink-0 rounded-full", DOT_CLASS[props.status])}
      aria-hidden="true"
    />
    {props.label}
    <Show when={props.children}>{props.children}</Show>
  </span>
);
